import React, { useCallback, useEffect, useMemo } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Typography,
} from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OilBarrelOutlinedIcon from '@mui/icons-material/OilBarrelOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import { useUtilityProofStore } from '../../stores/utility-proof.store';
import {
  UtilityType,
  UtilitySettingsSchema,
  UtilityMonthSchema,
} from '../../types/utility-proof.type';
import { listUtilityCatalog } from '../../services/utility-emission.service';
import {
  UtilityActivityType,
  UtilityCategory,
} from '../../types/utility-emission.type';
import UtilityProofRow from './UtilityProofRow';
import { yyyyMmToDate } from './BranchMonthToolbar';
import { useTranslation } from 'react-i18next';

function resolveRequiredUtilities(
  utilityMonth: UtilityMonthSchema | null,
  settings: UtilitySettingsSchema | null,
  fallback: UtilityType[]
): UtilityType[] {
  const snap = utilityMonth?.required_utilities_snapshot;
  if (Array.isArray(snap)) return snap;
  const req = settings?.required_utilities;
  if (Array.isArray(req)) return req;
  return fallback;
}

const UTILITY_ICONS: Record<UtilityCategory, React.ComponentType<SvgIconProps>> = {
  [UtilityCategory.ELECTRICITY]: BoltOutlinedIcon,
  [UtilityCategory.GAS]: LocalFireDepartmentOutlinedIcon,
  [UtilityCategory.OIL]: OilBarrelOutlinedIcon,
  [UtilityCategory.WATER]: WaterDropOutlinedIcon,
  [UtilityCategory.OTHER]: ScienceOutlinedIcon,
};

export interface UtilityProofsTabProps {
  siteId: string | null;
  selectedMonth: string;
  utilityTypes?: UtilityActivityType[];
}

const UtilityProofsTab: React.FC<UtilityProofsTabProps> = ({
  siteId,
  selectedMonth,
  utilityTypes,
}) => {
  const [catalog, setCatalog] = React.useState<UtilityActivityType[]>([]);
  const { t } = useTranslation();
  const {
    settings,
    utilityMonth,
    proofFiles,
    isLoadingMonthAndProofs,
    isLoadingSettings,
    uploadingUtilities,
    uploadErrors,
    monthError,
    settingsError,
    clearMonthError,
    clearSettingsError,
    clearUploadError,
    fetchSettings,
    fetchMonthAndProofs,
    resetMonthAndProofs,
    uploadProofsForUtility,
  } = useUtilityProofStore();

  const yearMonth = useMemo(() => {
    const d = yyyyMmToDate(selectedMonth);
    if (!d) return null;
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, [selectedMonth]);

  useEffect(() => {
    resetMonthAndProofs();
    if (!siteId || !yearMonth) return;
    void fetchSettings(siteId);
    void fetchMonthAndProofs(siteId, yearMonth.year, yearMonth.month);
    if (!utilityTypes) {
      void listUtilityCatalog().then(setCatalog);
    }
  // fetchSettings, fetchMonthAndProofs, resetMonthAndProofs are stable zustand actions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, selectedMonth, utilityTypes]);

  const handleRefresh = useCallback(() => {
    if (!siteId || !yearMonth) return;
    void fetchMonthAndProofs(siteId, yearMonth.year, yearMonth.month);
  }, [siteId, yearMonth, fetchMonthAndProofs]);

  const handleUpload = useCallback(
    (utilityType: UtilityType, files: File[]) => {
      if (!siteId || !yearMonth) return;
      void uploadProofsForUtility(
        siteId,
        yearMonth.year,
        yearMonth.month,
        utilityType,
        files
      );
    },
    [siteId, yearMonth, uploadProofsForUtility]
  );

  if (!siteId) {
    return (
      <Alert severity="info" icon={<StorefrontOutlinedIcon fontSize="inherit" />}>
        {t('dataEntry.proofs.selectBranch')}
      </Alert>
    );
  }

  if (!yearMonth) {
    return (
      <Alert severity="warning" icon={<StorefrontOutlinedIcon fontSize="inherit" />}>
        {t('dataEntry.proofs.invalidMonth')}
      </Alert>
    );
  }

  const isLoading = isLoadingSettings || isLoadingMonthAndProofs;

  if (!isLoadingSettings && settings?.utilities_not_required) {
    return (
      <Alert
        severity="info"
        icon={<BlockOutlinedIcon fontSize="inherit" />}
        sx={{ alignItems: 'flex-start' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('dataEntry.proofs.trackingNotRequiredTitle')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('dataEntry.proofs.trackingNotRequiredBody')}
        </Typography>
      </Alert>
    );
  }

  const isLocked = utilityMonth?.is_locked ?? false;
  const displayedUtilities = utilityTypes ?? catalog;
  const requiredUtilities = resolveRequiredUtilities(
    utilityMonth,
    settings,
    displayedUtilities.map((type) => type.key)
  );

  return (
    <Box>
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {utilityMonth && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1.5 }}
        >
          {t('dataEntry.reportingMonth')} {utilityMonth.month_key}
          {utilityMonth.opened_at
            ? ` · Opened ${new Date(utilityMonth.opened_at).toLocaleString()}`
            : null}
        </Typography>
      )}

      {settingsError && (
        <Alert severity="error" onClose={clearSettingsError} sx={{ mb: 2 }}>
          {settingsError}
        </Alert>
      )}
      {monthError && (
        <Alert severity="error" onClose={clearMonthError} sx={{ mb: 2 }}>
          {monthError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLocked && (
            <Chip
              icon={<LockOutlinedIcon />}
              label={t('dataEntry.proofs.monthLocked')}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
          {utilityMonth && !isLocked && (
            <Chip
              label={t('dataEntry.proofs.open')}
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <Button
          variant="text"
          size="small"
          startIcon={<RefreshOutlinedIcon />}
          onClick={handleRefresh}
          disabled={isLoading}
        >
          {t('dataEntry.proofs.refresh')}
        </Button>
      </Box>

      {isLocked && (
        <Alert
          severity="warning"
          icon={<LockOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          {t('dataEntry.proofs.monthLockedAlert')}
          {utilityMonth?.locked_at && (
            <> {t('dataEntry.proofs.lockedOn', { date: new Date(utilityMonth.locked_at).toLocaleDateString() })}</>
          )}
        </Alert>
      )}

      {displayedUtilities.map((utilityType, index) => (
        <React.Fragment key={utilityType.key}>
          {index > 0 && <Divider />}
          <UtilityProofRow
            utilityType={utilityType.key}
            label={utilityType.label}
            Icon={UTILITY_ICONS[utilityType.category]}
            files={proofFiles.filter((f) => f.utility_type === utilityType.key)}
            isRequired={requiredUtilities.includes(utilityType.key)}
            isLocked={isLocked}
            isUploading={Boolean(uploadingUtilities[utilityType.key])}
            uploadError={uploadErrors[utilityType.key] ?? null}
            onClearError={() => clearUploadError(utilityType.key)}
            onUpload={(files) => handleUpload(utilityType.key, files)}
          />
        </React.Fragment>
      ))}
    </Box>
  );
};

export default UtilityProofsTab;
